/**
 * 메일 「환경설정」 — /mail/settings (2026-09-05).
 *
 * 디자인 정본 **P2-1 설정 화면** 의 첫 채택이다 — 화면머리 → 탭(line) → 절 → 저장바.
 * 배치는 공용 `styles/settings-screen.css`(`.wc-set__*`) 한 벌을 쓴다.
 *
 * ★ 탭은 **주소에 남긴다**(`?tab=`). 새로고침·링크로 그 칸이 다시 열려야 한다(정본 P2-1).
 * ★ **여덟 탭이 다 찼다**(2026-09-06). 「아직 만들지 않았습니다」 자리는 이제 없다 —
 *   모르는 `?tab=` 값은 위에서 첫 칸으로 떨어지므로 빈 화면도 안 생긴다.
 *
 * ★★ **셸 안의 오른쪽 칸이다** (2026-09-05 사용자 지시). 처음엔 쓰기 화면처럼 제 화면으로
 *    세웠더니 **좌측 메일함이 통째로 사라졌다** — 설정을 보는 동안에도 어느 메일함이 있는지는
 *    남아 있어야 한다. `inShell` 이면 `.mail-main` 을 채우고 **제 안에서 흐른다**(그 자리에는
 *    바깥 스크롤이 없다). 안 주면 종전처럼 바깥 스크롤 + 붙박이 저장바로 선다.
 */

import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from '../../icons';
import { Button } from '../../components/shared/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { Tabs, type TabItem } from '../../components/shared/Tabs';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailSettingsBasic } from './MailSettingsBasic';
import { MailSettingsFolders } from './MailSettingsFolders';
import { MailSettingsSpam } from './MailSettingsSpam';
import { MailSettingsFilter } from './MailSettingsFilter';
import { MailSettingsForward } from './MailSettingsForward';
import { MailSettingsVacation } from './MailSettingsVacation';
import { MailSettingsExternal } from './MailSettingsExternal';
import { MailSettingsRecent } from './MailSettingsRecent';
import '../../styles/settings-screen.css';
import '../../styles/mail-tag.css';
import './MailSettings.css';

/** 탭 여덟 — 순서는 사용자가 지정한 그대로다. */
const TAB_KEYS: { value: string; labelKey: MailI18nKey }[] = [
  { value: 'basic', labelKey: 'mail.settings.tab.basic' },
  { value: 'folder', labelKey: 'mail.settings.tab.folder' },
  { value: 'spam', labelKey: 'mail.settings.tab.spam' },
  { value: 'filter', labelKey: 'mail.settings.tab.filter' },
  { value: 'forward', labelKey: 'mail.settings.tab.forward' },
  { value: 'vacation', labelKey: 'mail.settings.tab.vacation' },
  { value: 'external', labelKey: 'mail.settings.tab.external' },
  { value: 'recent', labelKey: 'mail.settings.tab.recent' },
];

export interface MailSettingsPageProps {
  /** 메일 셸(`.mail-main`) 안에서 칸을 채울 때. 안 주면 홀로 선 화면이다. */
  inShell?: boolean;
}

export function MailSettingsPage({ inShell = false }: MailSettingsPageProps = {}) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // 모르는 값이 주소에 있으면 첫 칸으로 — 빈 화면이 뜨지 않는다.
  const raw = params.get('tab');
  const tab = TAB_KEYS.some((t) => t.value === raw) ? (raw as string) : 'basic';

  const pickTab = useCallback((next: string) => {
    // replace 로 바꾼다 — 탭을 여섯 번 누르고 뒤로 가면 여섯 번 눌러야 나가는 것을 막는다.
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: true });
  }, [setParams]);

  const items: TabItem[] = TAB_KEYS.map((t) => ({ value: t.value, label: tMail(t.labelKey) }));

  return (
    <div className={`mailset wc-set${inShell ? ' mailset--fill wc-set--fill' : ''}`}>
      {/*
        ★★★ 셸 안에서는 **머리를 여기서 그리지 않는다** (2026-09-08 사용자 지적).
           머리는 판(`.mail-main`) «밖»에 서야 하는데 이 컴포넌트는 판 «안»이다 —
           그래서 셸일 때는 `MailPage` 가 목록 화면과 **같은 자리**에 세운다.
           경로(「메일 › 환경설정」)도 거기서 붙는다.
        ★ 주소로 이 화면만 따로 열었을 때(`inShell` 아님)는 제 머리를 그대로 그린다.
      */}
      {!inShell && (
        <PageHeader
          title={tMail('mail.settings.title')}
          description={tMail('mail.settings.desc')}
          actions={(
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<ArrowLeft size={14} aria-hidden />}
              onClick={() => navigate('/mail')}
            >
              {tMail('mail.settings.back')}
            </Button>
          )}
        />
      )}

      <Tabs
        className="wc-set__tabs"
        items={items}
        value={tab}
        onChange={pickTab}
        variant="line"
        ariaLabel={tMail('mail.settings.tabs_aria')}
      />

      {tab === 'basic' ? (
        <MailSettingsBasic />
      ) : tab === 'folder' ? (
        <MailSettingsFolders />
      ) : tab === 'spam' ? (
        <MailSettingsSpam />
      ) : tab === 'filter' ? (
        <MailSettingsFilter />
      ) : tab === 'forward' ? (
        <MailSettingsForward />
      ) : tab === 'vacation' ? (
        <MailSettingsVacation />
      ) : tab === 'external' ? (
        <MailSettingsExternal />
      ) : (
        <MailSettingsRecent />
      )}
    </div>
  );
}

export default MailSettingsPage;
